import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import type { Organization } from '@/types/events';

export function useUserEvents(organizationId: string | null) {
  const [events, setEvents] = useState<Organization[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchUserEvents = useCallback(async () => {
    if (!organizationId) {
      setEvents([]);
      return;
    }
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('events')
        .select(
          'id, name, image, location, description, start_date, end_date, status, slots, departments, time_intervals, is_public, created_by, created_at, is_active'
        )
        .eq('created_by', organizationId)
        .order('created_at', { ascending: false });

      if (error) throw error;

      const eventsList = data ?? [];
      if (eventsList.length === 0) {
        setEvents([]);
        return;
      }

      const eventIds = eventsList.map((e) => e.id);
      const { data: volunteersData } = await supabase
        .from('volunteers')
        .select('event_id')
        .in('event_id', eventIds)
        .eq('is_active', true);

      const countByEventId: Record<string, number> = {};
      for (const row of volunteersData ?? []) {
        const id = (row as { event_id: string }).event_id;
        countByEventId[id] = (countByEventId[id] ?? 0) + 1;
      }

      const withCounts = eventsList.map(
        (event) =>
          ({
            ...event,
            volunteerCount: countByEventId[event.id] ?? 0,
            location: event.location ?? '',
          }) as Organization
      );
      setEvents(withCounts);
    } catch {
      setEvents([]);
    } finally {
      setLoading(false);
    }
  }, [organizationId]);

  useEffect(() => {
    fetchUserEvents();
  }, [fetchUserEvents]);

  return { events, loading, refetch: fetchUserEvents };
}
